import ProtectedRoute from '@/auth/ProtectedRoute';
import PublicOnlyRoute from '@/auth/PublicOnlyRoute';
import ConsoleLayout from '@/components/layout/ConsoleLayout';
import {
  navSetting,
  navSettingWorkspace,
} from '@/components/layout/navigation/configNav';
import SignIn from '@/pages/auth/signin';
import VSCodeCallback from '@/pages/auth/vscode';
import Workspaces from '@/pages/dashboard/workspaces';
import CreateWorkspace from '@/pages/dashboard/workspaces/create';
import EditWorkspace from '@/pages/dashboard/workspaces/edit';
import Organization from '@/pages/dashboard/workspaces/organization';
import Home from '@/pages/Home';
import Onboard from '@/pages/onboard';
import Payment from '@/pages/setting/payment';
import Profile from '@/pages/setting/profile';
import Provider from '@/provider';
import ProtectOnBoard from '@/provider/ProtectOnBoard';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouteObject,
} from 'react-router-dom';
import WorkspaceSettingLayout from './wrapper/WorkspaceSettingLayout';
import DashboardOverview from '@/pages/dashboard/overview';
import PaymentOverview from '@/pages/setting/payment/overview';
import LLMOverview from '@/pages/dashboard/llm/overview';
import LLMCredentials from '@/pages/dashboard/llm/credentials';
import LLMActivity from '@/pages/dashboard/llm/activity';
import LLMServiceLayout from './wrapper/LLMServiceLayout';
import ServiceQuota from '@/pages/setting/service-quota';
import ErrorBoundary from '@/pages/error/ErrorBoundary';
import SettingLayout from './wrapper/SettingLayout';
import BalanceHistory from '@/pages/setting/payment/balance-history';
import OneClickDeployView from '@/pages/dashboard/one-click-deploy/view';
import OneClickDeployLayout from './wrapper/OneClickDeployLayout';
import OneClickDeployCreate from '@/pages/dashboard/one-click-deploy/create';
import OneClickDeployDetail from '@/pages/dashboard/one-click-deploy/detail';
import OneClickDeployDetailDeployments from '@/pages/dashboard/one-click-deploy/detail/deployments';
import OneClickDeployDetailSetting from '@/pages/dashboard/one-click-deploy/detail/setting';
import OneClickDeployDetailOverview from '@/pages/dashboard/one-click-deploy/detail/overview';
import OneClickDeployDetailUsage from '@/pages/dashboard/one-click-deploy/detail/usage';
// import UsageCost from '@/pages/setting/payment/usage-cost';
import OneClickDeployDetailActivity from '@/pages/dashboard/one-click-deploy/detail/activity';
import Onboarding from '@/pages/dashboard/serverless-gpu/onboarding';
import ServerlessLayout from '@/pages/dashboard/serverless-gpu/layout';
import Authtoken from '@/pages/setting/token';
import ServerlessTasks from '@/pages/dashboard/serverless-gpu/tasks';
import ServerlessOverview from '@/pages/dashboard/serverless-gpu/overview';
import ServerlessDeployment from '@/pages/dashboard/serverless-gpu/detail/deployment';
import ServerlessGpuDevelop from '@/pages/dashboard/serverless-gpu/detail/develop';
import ServerlessDetailLayout from '@/pages/dashboard/serverless-gpu/detail/layout';
import ServerlessOverviewDetail from '@/pages/dashboard/serverless-gpu/detail/overview';
import ServerlessDetailTasks from '@/pages/dashboard/serverless-gpu/detail/tasks';
import ServerlessStorageLogs from '@/pages/dashboard/serverless-gpu/detail/storage-logs';
import ServerlessGPUDetailStorage from '@/pages/dashboard/serverless-gpu/detail/storage';
import siteConfig from '@/config';
import ServerlessTokens from '@/pages/dashboard/serverless-gpu/tokens';
import ServerlessStorageLogsOverview from '@/pages/dashboard/serverless-gpu/storage-logs';
import ServerlessActivity from '@/pages/dashboard/serverless-gpu/detail/activity';
import InvoiceHistory from '@/pages/setting/payment/invoice';
import ServerlessGPUPricing from '@/pages/dashboard/serverless-gpu/pricing';
import ServerlessGPUPlayground from '@/pages/dashboard/serverless-gpu/detail/playground';
import ServerlessGPUCreateProject from '@/pages/dashboard/serverless-gpu/create-project';
import Credit from '@/pages/setting/payment/credit';
import Coupon from '@/pages/setting/coupon';
import ServerlessByProjectSetting from '@/pages/dashboard/serverless-gpu/detail/setting';
import ServerlessBlueprintOverview from '@/pages/dashboard/serverless-gpu/blueprint/overview';
import ServerlessBlueprintById from '@/pages/dashboard/serverless-gpu/blueprint/byId';
import ServerlessGPUPlaygroundBlueprint from '@/pages/dashboard/serverless-gpu/detail/playground-blueprint';
import AiServiceLayout from '@/pages/dashboard/ai/layout';
import AiListModule from '@/pages/dashboard/ai/overview';
import AiCredentials from '@/pages/dashboard/ai/credentials';
import AiServiceOCRLayout from '@/pages/dashboard/ai/services/typhoon-ocr/layout';
import AiServiceOCROverview from '@/pages/dashboard/ai/services/typhoon-ocr/overview';
import AiServiceOCRPlayground from '@/pages/dashboard/ai/services/typhoon-ocr/playground';
import AiServiceOCRPricing from '@/pages/dashboard/ai/services/typhoon-ocr/pricing';
import AiServiceOCRActivity from '@/pages/dashboard/ai/services/typhoon-ocr/activity';
import CancelPage from '@/pages/cancel';
// GPU Instance imports
import GPUInstanceLayout from '@/pages/dashboard/gpu-instance/layout';
import GPUInstanceOverview from '@/pages/dashboard/gpu-instance/overview';
import GPUInstanceCreate from '@/pages/dashboard/gpu-instance/create';
import GPUInstanceList from '@/pages/dashboard/gpu-instance/instances';
import GPUInstancePricing from '@/pages/dashboard/gpu-instance/pricing';
import GPUInstanceDetailLayout from '@/pages/dashboard/gpu-instance/detail/layout';
import GPUInstanceDetailOverview from '@/pages/dashboard/gpu-instance/detail/overview';
import GPUInstanceDetailTasks from '@/pages/dashboard/gpu-instance/detail/tasks';
import GPUInstanceDetailSettings from '@/pages/dashboard/gpu-instance/detail/settings';
// import { SiteConfigService } from '@/types/config';

const { services } = siteConfig;

type ServiceRoute = RouteObject & {
  service?: boolean | undefined;
  children?: ServiceRoute[];
};

const filterServiceRoutes = (routes: ServiceRoute[]): ServiceRoute[] => {
  return routes.filter((route) => {
    if ('service' in route) {
      return route.service;
    }

    if (route.children) {
      const filteredChildren = filterServiceRoutes(route.children);
      route.children = filteredChildren;
      return filteredChildren.length > 0;
    }

    return true;
  });
};

const routes: ServiceRoute[] = [
  {
    element: <Provider />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: 'auth',
        element: (
          <PublicOnlyRoute>
            <Outlet />
          </PublicOnlyRoute>
        ),
        children: [
          {
            path: 'sign-in',
            element: <SignIn />,
          },
        ],
      },
      {
        // VS Code auth callback - outside PublicOnlyRoute since authenticated users need access
        path: 'auth/vscode',
        element: <VSCodeCallback />,
      },
      {
        path: '/',
        element: (
          <ProtectedRoute>
            <ProtectOnBoard>
              <Outlet />
            </ProtectOnBoard>
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="/setting/workspaces" replace />,
          },
          {
            path: 'cancel',
            element: <CancelPage />,
          },
          {
            path: 'dashboard',
            children: [
              {
                path: 'new',
                service: siteConfig.services.workspace?.create,
                element: <CreateWorkspace />,
              },
              {
                path: ':workspaceId',
                element: (
                  <ConsoleLayout>
                    <Outlet />
                  </ConsoleLayout>
                ),
                children: [
                  {
                    index: true,
                    element: <DashboardOverview />,
                  },
                  {
                    path: 'llm-service',
                    service: siteConfig.services.llm,
                    element: <LLMServiceLayout />,
                    children: [
                      {
                        index: true,
                        element: <LLMOverview />,
                      },
                      {
                        path: 'credentials',
                        element: <LLMCredentials />,
                      },
                      {
                        path: 'activity',
                        element: <LLMActivity />,
                      },
                    ],
                  },

                  {
                    path: 'one-click-deploy',
                    service: siteConfig.services.oneClickDeploy,
                    element: <OneClickDeployLayout />,
                    children: [
                      {
                        index: true,
                        element: <OneClickDeployView />,
                      },
                      {
                        path: 'create',
                        element: <OneClickDeployCreate />,
                      },
                      {
                        path: ':instanceId',
                        element: <OneClickDeployDetail />,
                        children: [
                          {
                            index: true,
                            element: <OneClickDeployDetailOverview />,
                          },
                          {
                            path: 'deployments',
                            element: <OneClickDeployDetailDeployments />,
                          },
                          {
                            path: 'usage',
                            element: <OneClickDeployDetailUsage />,
                          },
                          {
                            path: 'activity',
                            element: <OneClickDeployDetailActivity />,
                          },
                          {
                            path: 'setting',
                            element: <OneClickDeployDetailSetting />,
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: 'serverless-gpu',
                    service: siteConfig.services.serverlessGPU,
                    element: <ServerlessLayout />,
                    children: [
                      {
                        index: true,
                        element: <Navigate to="onboarding" replace />,
                      },

                      {
                        path: 'overview',
                        element: <ServerlessOverview />,
                      },
                      {
                        path: 'onboarding',
                        element: <Onboarding />,
                      },
                      {
                        path: 'tasks',
                        element: <ServerlessTasks />,
                      },
                      {
                        path: 'tokens',
                        element: <ServerlessTokens />,
                      },
                      {
                        path: 'storage-logs',
                        element: <ServerlessStorageLogsOverview />,
                      },
                      {
                        path: 'pricing',
                        element: <ServerlessGPUPricing />,
                      },
                      {
                        path: 'create-project',
                        element: <ServerlessGPUCreateProject />,
                      },
                      {
                        path: 'blueprint',
                        children: [
                          {
                            index: true,
                            element: <ServerlessBlueprintOverview />,
                          },
                          {
                            path: 'item',
                            element: <ServerlessBlueprintById />,
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: 'serverless-gpu/:projectId',
                    service: siteConfig.services.serverlessGPU,
                    element: <ServerlessDetailLayout />,
                    children: [
                      {
                        index: true,
                        element: <Navigate to="overview" replace />,
                      },
                      {
                        path: 'overview',
                        element: <ServerlessOverviewDetail />,
                      },
                      {
                        path: 'onboarding',
                        element: <Onboarding />,
                      },
                      {
                        path: 'tasks',
                        element: <ServerlessDetailTasks />,
                      },
                      {
                        path: 'storage-logs',
                        element: <ServerlessStorageLogs />,
                      },
                      {
                        path: 'deploy',
                        element: <ServerlessDeployment />,
                      },
                      {
                        path: 'develop',
                        element: <ServerlessGpuDevelop />,
                      },
                      {
                        path: 'storage',
                        element: <ServerlessGPUDetailStorage />,
                      },
                      {
                        path: 'activity',
                        element: <ServerlessActivity />,
                      },
                      {
                        path: 'playground',
                        element: <ServerlessGPUPlayground />,
                      },
                      {
                        path: 'playground-blueprint',
                        element: <ServerlessGPUPlaygroundBlueprint />,
                      },
                      {
                        path: 'setting',
                        element: <ServerlessByProjectSetting />,
                      },
                    ],
                  },
                  {
                    path: 'ai',
                    service: siteConfig.services.ai,
                    element: <AiServiceLayout />,
                    children: [
                      {
                        index: true,
                        element: <Navigate to="overview" replace />,
                      },
                      {
                        path: 'overview',
                        element: <AiListModule />,
                      },
                      {
                        path: 'credentials',
                        element: <AiCredentials />,
                      },
                    ],
                  },
                  {
                    path: 'ai/typhoon-ocr',
                    service: siteConfig.services.ai,
                    element: <AiServiceOCRLayout />,
                    children: [
                      {
                        index: true,
                        element: <Navigate to="overview" replace />,
                      },
                      {
                        path: 'overview',
                        element: <AiServiceOCROverview />,
                      },
                      // {
                      //   path: 'playground',
                      //   element: <AiServiceOCRPlayground />,
                      // },
                      {
                        path: 'activity',
                        element: <AiServiceOCRActivity />,
                      },
                      {
                        path: 'pricing',
                        element: <AiServiceOCRPricing />,
                      },
                    ],
                  },
                  {
                    path: 'gpu-instance',
                    service: siteConfig.services.gpuInstance,
                    element: <GPUInstanceLayout />,
                    children: [
                      {
                        index: true,
                        element: <Navigate to="overview" replace />,
                      },
                      {
                        path: 'overview',
                        element: <GPUInstanceOverview />,
                      },
                      {
                        path: 'create',
                        element: <GPUInstanceCreate />,
                      },
                      {
                        path: 'instances',
                        element: <GPUInstanceList />,
                      },
                      {
                        path: 'pricing',
                        element: <GPUInstancePricing />,
                      },
                    ],
                  },
                  {
                    path: 'gpu-instance/:instanceId',
                    service: siteConfig.services.gpuInstance,
                    element: <GPUInstanceDetailLayout />,
                    children: [
                      {
                        index: true,
                        element: <Navigate to="overview" replace />,
                      },
                      {
                        path: 'overview',
                        element: <GPUInstanceDetailOverview />,
                      },
                      {
                        path: 'tasks',
                        element: <GPUInstanceDetailTasks />,
                      },
                      {
                        path: 'settings',
                        element: <GPUInstanceDetailSettings />,
                      },
                    ],
                  },
                  {
                    path: 'setting',
                    element: <WorkspaceSettingLayout />,
                    children: [
                      {
                        path: 'general',
                        element: <EditWorkspace />,
                      },
                      {
                        path: 'organization',
                        element: <Organization />,
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: 'setting',
            element: <SettingLayout />,
            children: [
              {
                path: 'profile',
                element: <Profile />,
              },
              {
                path: 'tokens',
                element: <Authtoken />,
              },
              {
                path: 'payment',
                isHidden: !siteConfig.services.setting?.payment,
                element: <Payment />,
                children: [
                  {
                    index: true,
                    element: <Navigate to="overview" replace />,
                  },
                  {
                    path: 'overview',
                    element: <PaymentOverview />,
                  },
                  {
                    path: 'balance-history',
                    element: <BalanceHistory />,
                  },
                  {
                    path: 'invoice',
                    element: <InvoiceHistory />,
                  },
                  {
                    path: 'credit',
                    element: <Credit />,
                  },
                  // {
                  //   path: 'usage',
                  //   element: <UsageCost />,
                  // },
                ],
              },
              {
                path: 'service-quota',
                isHidden: !siteConfig.services.setting?.quota,
                element: <ServiceQuota />,
              },
              {
                path: 'workspaces',
                element: <Workspaces />,
              },
              {
                path: 'coupon',
                element: <Coupon />,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
];

export const router = createBrowserRouter(filterServiceRoutes(routes));

export default router;
